import {Component, DoCheck, inject, Input, OnDestroy, OnInit, signal, WritableSignal} from '@angular/core';
import {MediaHelperService} from '../../core/media-helper.service';
import {SearchObject} from '../../core/definitions/search-object';
import {BaseModel} from "../../core/definitions/base-model";

import {SnackbarObject} from "../../core/definitions/snackbar.object";
import {MediaConversionWatcher} from '../media-conversion-watcher.service';
import {SuperObjectModel} from '../../core/definitions/super-object-model';
import {isMediaNotReady, NOT_READY_THUMBNAIL_ICON} from '../thumbnail-fallback-icon';

@Component({
    selector: 'app-thumbnail',
    templateUrl: './thumbnail.component.html',
    styleUrls: ['./thumbnail.component.scss'],
    standalone: false
})
export class ThumbnailComponent implements OnInit, DoCheck, OnDestroy {
  // Injections
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);
  // Only provided where the list follows media conversion, e.g. the object content list
  private readonly conversionWatcher: MediaConversionWatcher = inject(MediaConversionWatcher, {optional: true});

  public imgClass = '';

  @Input() item: BaseModel;
  @Input() viewMode: string;
  @Input() imageIdField: string;
  @Input() stylingClass: string;
  @Input() _snackBar: WritableSignal<SnackbarObject>;
  // Only the upload views show the upload result badge; lists that follow conversion also set $$dmsStatus
  @Input() showUploadStatus = false;

  readonly notReadyIcon = NOT_READY_THUMBNAIL_ICON;
  imageLoadError = signal(false);
  private failedUrl: string;
  private lastDmsStatus: string;
  private urlLookupRetried = false;

  ngOnInit() {
    if (this.item) {
      this.lookupThumbnailUrl();
    }
  }

  ngDoCheck() {
    this.retryLookupWhenMediaIsDone();
    // A refreshed url is tried again
    if (this.imageLoadError() && this.item?.$$thumbnailUrl !== this.failedUrl) {
      this.imageLoadError.set(false);
    }
  }

  onImageError() {
    this.failedUrl = this.item.$$thumbnailUrl;
    this.imageLoadError.set(true);
    this.conversionWatcher?.watch(<SuperObjectModel>this.item, this.getMediaId());
  }

  // A failed thumbnail is only broken once the watcher knows the media is not converting
  get showBrokenImage(): boolean {
    return this.imageLoadError() && !this.conversionWatcher?.isAwaitingStatus(this.getMediaId());
  }

  ngOnDestroy() {
    // This will stop upload status timeout started in mediaHelper
    this.mediaHelper.cancelUploadWait(<SearchObject>this.item);
  }

  private lookupThumbnailUrl() {
    this.mediaHelper.setThumbnailUrl(<SearchObject>this.item, this.imageIdField, this._snackBar)
      .catch(() => undefined)
      .then(() => {
        this.showImageOrPlaceholder();
        this.watchWhenNoUrl();
      });
  }

  // DMS may not know a new video yet, so the lookup gives no url; follow its conversion like a failed img.
  // Without a watcher or media id the item legitimately has no image.
  private watchWhenNoUrl() {
    if (this.conversionWatcher && this.getMediaId() && !this.item.$$thumbnailUrl && !this.item.$$uploading) {
      this.onImageError();
    }
  }

  // Once, when the media turns done and no url was found earlier
  private retryLookupWhenMediaIsDone() {
    const status = this.item?.$$dmsStatus;
    const turnedDone = status === 'done' && isMediaNotReady(this.lastDmsStatus);
    this.lastDmsStatus = status;
    if (turnedDone && !this.item.$$thumbnailUrl && !this.urlLookupRetried) {
      this.urlLookupRetried = true;
      this.lookupThumbnailUrl();
    }
  }

  private getMediaId(): string {
    const item = <SearchObject>this.item;
    const objectType = item.object_type?.toLowerCase();
    return objectType === 'attachment' || objectType === 'video' ? item.artifact_id : item.thumbnail_id;
  }

  private showImageOrPlaceholder() {
    this.imgClass = this.stylingClass ? this.stylingClass + ' ' : '';
    if (this.item.$$thumbnailUrl) {
      if (this.viewMode && this.viewMode === 'thumbnail') {
        this.imgClass += 'searchThumbnailBig';
      } else {
        this.imgClass += 'searchThumbnail';
      }
    } else {
      if (this.viewMode && this.viewMode === 'thumbnail') {
        this.imgClass += 'imgPlaceholderBig';
      } else {
        this.imgClass += 'imgPlaceholder';
      }
    }
  }

}
